import AlertBanner from 'tio-ui/components/alert-banner';
import Button from 'tio-ui/components/button';
import { Section, VStack } from 'tio-ui/components/layout';
import PageHero from 'tio-ui/components/page-hero';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import AccountTable from 'tio-employee/components/accounts/account-table';
import BenefitCards from 'tio-employee/components/accounts/benefit-cards';
import DocumentsTable from 'tio-employee/components/observability/documents-table';
import { uploadIntentFor } from 'tio-employee/utils/observability/upload-intent';
import LoanAccountCard from 'tio-employee/components/accounts/loan-account-card';
import Card from 'tio-ui/components/card';
import { concat } from '@ember/helper';
import style from 'ember-style-modifier/modifiers/style';
import type { TOC } from '@ember/component/template-only';
import type SessionContextService from 'tio-employee/services/session-context';
import type IntlService from 'ember-intl/services/intl';
import ChangeActiveAccountModal from 'tio-common/components/account/change-active-account-modal';
import Component from '@glimmer/component';
import pageTitle from 'ember-page-title/helpers/page-title';
import RouterService from '@ember/routing/router-service';
import StatCard from 'tio-ui/components/stat-card';
import Table from 'tio-ui/components/table';
import Text from 'tio-ui/components/text';
import { formatMonthYearLong } from 'tio-ui/format';
import Heading from 'tio-ui/components/heading';
import type { AccountExtension as AccountModel } from 'tio-data/data/resources/account.ext';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type PartnerService from 'tio-employee/services/partner';
import { PhBank } from 'ember-phosphor-icons';
import { and } from 'tio-ui/utilities';
import type { AccountActivityAccountsRouteModel } from 'tio-employee/routes/authenticated/account-activity/accounts';
import type BetaExperienceService from 'tio-employee/services/beta-experience';

/** A metric tile inside the hero on the nested card the TA and home heroes use: label, value, optional bar, caption. */
const HeroTile: TOC<{
  Args: { label: string; value: string; caption?: string; percent?: number };
}> = <template>
  <Card @radius="nested" @padding="sm" role="group" aria-label={{@label}}>
    <div class="tio-col-extra-tight">
      <Text @variant="muted" @size="xs">{{@label}}</Text>
      <div class="tio-text-2xl-bold tio-fg-primary">{{@value}}</div>
      {{#if @percent}}
        <div
          class="w-full h-2 tio-bg-track rounded-full overflow-hidden flex"
          role="progressbar"
          aria-valuenow={{@percent}}
          aria-valuemin={{0}}
          aria-valuemax={{100}}
          aria-label={{@label}}
        >
          <div class="h-full app-progress-fill--brand" {{style width=(concat @percent "%")}}></div>
        </div>
      {{/if}}
      {{#if @caption}}
        <Text @variant="muted" @size="xs">{{@caption}}</Text>
      {{/if}}
    </div>
  </Card>
</template>;

interface RouteSignature {
  Args: {
    model: AccountActivityAccountsRouteModel;
  };
}

export default class AccountActivityAccountRouteComponent extends Component<RouteSignature> {
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare partner: PartnerService;
  @service('beta-experience') declare betaExperience: BetaExperienceService;
  @service declare sessionContext: SessionContextService;

  @tracked showChangeAccountModal = false;

  /** The beta experience adds overview, contribution and benefit zones to the shipped frame. */
  get isBeta() {
    return this.betaExperience.isEnabled;
  }

  get person(): PersonModel {
    return this.args.model.person;
  }

  get activeAccounts() {
    return this.person?.activeAccounts || [];
  }

  get isSlr() {
    return this.person.employees.some((employee) => {
      return employee.isActive && !!employee.plan && employee.plan.cadence !== 'NEVER';
    });
  }

  get accountReceivingContributions() {
    if (!this.isSlr) {
      return null;
    }
    return this.activeAccounts.find(
      (account) => account.id === this.person?.accountReceivingContributionsId
    );
  }

  get paidAccounts() {
    return this.person?.paidAccounts || [];
  }

  // gets unpaid accounts that are not receiving contributions
  get otherActiveAccount() {
    return this.activeAccounts
      .filter(
        (account) => !account.isPaidOff && account.id! !== this.accountReceivingContributions?.id
      )
      .sort((a, b) => a.publicOrdinalValue - b.publicOrdinalValue);
  }

  get loans() {
    return this.activeAccounts.map((account) => account.activeLoans).flat() || [];
  }

  get loanAccounts() {
    return this.args.model.loanAccounts;
  }

  get totalBalance() {
    return this.loanAccounts.reduce((sum, loan) => sum + loan.balance, 0);
  }

  get originalBalance() {
    return this.loanAccounts.reduce((sum, loan) => sum + loan.originalBalance, 0);
  }

  get paidOff() {
    return Math.max(0, this.originalBalance - this.totalBalance);
  }

  get progressPercent() {
    if (this.originalBalance <= 0) return 0;
    return Math.min(100, Math.round((this.paidOff / this.originalBalance) * 100));
  }

  /** Employer contributions this year when they are live; otherwise the principal paid down. */
  get paidThisYear() {
    return this.employerContributions?.yearToDate ?? this.paidOff;
  }

  /** The latest estimated payoff across accounts: the month the last loan clears. */
  get debtFreeDate() {
    const latest = this.loanAccounts
      .map((loan) => new Date(loan.estimatedPayoff).getTime())
      .filter((time) => !Number.isNaN(time))
      .sort((x, y) => y - x)[0];
    return latest ? formatMonthYearLong(new Date(latest)) : '';
  }

  get monthlyPayment() {
    return this.loanAccounts.reduce((sum, loan) => sum + loan.monthlyPayment, 0);
  }

  /** Balance-weighted across accounts, to one decimal. */
  get averageInterestRate() {
    const total = this.totalBalance;
    if (total <= 0) {
      return 0;
    }
    const weighted = this.loanAccounts.reduce(
      (sum, loan) => sum + loan.interestRate * loan.balance,
      0
    );
    return Math.round((weighted / total) * 10) / 10;
  }

  get loanCount() {
    return this.loanAccounts.reduce((sum, loan) => sum + loan.loanCount, 0);
  }

  /** A USD amount in the active locale; `digits` fixes the fraction digits when given. */
  money = (value: number | undefined | null, digits?: number) =>
    this.intl.formatNumber(value || 0, {
      style: 'currency',
      currency: 'USD',
      ...(digits === undefined
        ? {}
        : { minimumFractionDigits: digits, maximumFractionDigits: digits }),
    });

  /** Time-of-day greeting with the person's first name, as the TA and home heroes do. */
  get greeting() {
    const hour = new Date().getHours();
    const key = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
    const base = this.intl.t(`accounts.loan_summary.greeting_${key}`);
    const name = this.person.firstName;
    return name ? `${base}, ${name}` : base;
  }

  get employerName() {
    return (
      this.sessionContext.company?.name || this.sessionContext.currentEmployee?.company?.name || ''
    );
  }

  get employerContributions() {
    return this.args.model.employerContributions;
  }

  get projectedMonthlyContribution() {
    return Math.round((this.employerContributions?.projectedAnnual ?? 0) / 12);
  }

  /** An SLR member with linked accounts but none chosen to receive contributions. */
  get needsReceivingAccount() {
    return this.isSlr && this.activeAccounts.length > 0 && !this.accountReceivingContributions;
  }

  /** Accounts whose servicer link has lapsed; a fresh statement or NSLDS file restores them. */
  get accountsInRelink() {
    return this.activeAccounts.filter((account) => account.inRelink);
  }

  get attentionCount() {
    return (
      (this.activeAccounts.length ? 0 : 1) +
      (this.needsReceivingAccount ? 1 : 0) +
      this.accountsInRelink.length
    );
  }

  get hasBenefitCards() {
    const rows = this.args.model.benefitRows;
    return (
      rows.tuitionAssistance ||
      rows.wellness ||
      rows.pslf ||
      rows.slr ||
      rows.learningPartners ||
      rows.syf ||
      rows.idr
    );
  }

  get showContributionSelector() {
    return this.activeAccounts.length > 1;
  }

  get uploadQueryParams() {
    return uploadIntentFor(this.person, this.partner);
  }

  isReceivingContributions = (account: AccountModel) => {
    return this.person.accountReceivingContributionsId === account.id;
  };

  toggleChangeAccountModal = () => {
    this.showChangeAccountModal = !this.showChangeAccountModal;
  };

  reloadRecords = () => {
    this.router.refresh('authenticated.account-activity.accounts');
  };

  <template>
    {{pageTitle "Student Loan Summary"}}
    <VStack class="gap-8">
      {{#if this.isBeta}}
        <PageHero
          @gradient="brand-soft"
          class="tio-elevation-card-raised"
          data-test-loan-summary-hero
        >
          <:default>
            <div class="tio-col-extra-loose">
              <div class="tio-row-start-between">
                <div class="max-w-2xl tio-col-tight">
                  <div class="tio-row-tight tio-text-sm-semibold app-text-brand">
                    <PhBank class="tio-icon-sm" />
                    <span>{{this.greeting}}</span>
                  </div>
                  <Heading @level={{1}}>{{t "accounts.loan_summary.hero_title"}}</Heading>
                  {{#if this.loanAccounts.length}}
                    <Text class="tio-fg-secondary">
                      {{t
                        "accounts.loan_summary.hero_lede"
                        percent=this.progressPercent
                        date=this.debtFreeDate
                      }}
                    </Text>
                  {{/if}}
                </div>
                <LinkTo
                  @route="authenticated.observability.upload"
                  @query={{this.uploadQueryParams}}
                  class="tio-text-sm-semibold whitespace-nowrap app-text-link app-text-link--icon"
                  data-test-loan-summary-add-account
                >
                  {{t "common.accounts.add_account"}}
                </LinkTo>
              </div>
              {{#if this.loanAccounts.length}}
                <div class="app-card-grid app-card-grid--medium" data-test-loan-progress>
                  <HeroTile
                    @label={{t "accounts.loan_summary.total_balance"}}
                    @value={{this.money this.totalBalance 0}}
                    @caption={{t
                      "accounts.loan_summary.account_count"
                      count=this.activeAccounts.length
                    }}
                  />
                  <HeroTile
                    @label={{t "accounts.loan_summary.progress.paid_this_year"}}
                    @value={{this.money this.paidThisYear 0}}
                    @caption={{if
                      this.employerContributions
                      (t
                        "accounts.loan_summary.progress.lifetime_caption"
                        amount=(this.money this.employerContributions.lifetime 0)
                      )
                      (t "accounts.loan_summary.progress.paid_down_caption")
                    }}
                  />
                  <HeroTile
                    @label={{t "accounts.loan_summary.progress.label"}}
                    @value={{concat this.progressPercent "%"}}
                    @percent={{this.progressPercent}}
                    @caption={{t
                      "accounts.loan_summary.progress.remaining"
                      amount=(this.money this.totalBalance 0)
                    }}
                  />
                  <HeroTile
                    @label={{t "accounts.loan_summary.progress.debt_free_date"}}
                    @value={{this.debtFreeDate}}
                    @caption={{t "accounts.loan_summary.progress.estimated"}}
                  />
                </div>
              {{/if}}
            </div>
          </:default>
        </PageHero>
      {{else}}
        <PageHero
          @title={{t "accounts.my_accounts"}}
          @gradient="brand-soft"
          class="tio-elevation-card-raised"
        >
          <:actions>
            <Button
              @route="authenticated.observability.upload"
              @query={{this.uploadQueryParams}}
              @intent="primary"
              @appearance="outlined"
            >
              {{t "common.accounts.add_account"}}
            </Button>
          </:actions>
        </PageHero>
      {{/if}}

      {{#if (and this.isBeta this.attentionCount)}}
        <Section data-test-loan-attention>
          <:header>
            {{t "accounts.loan_summary.attention.title" count=this.attentionCount}}
          </:header>
          <:body>
            <VStack class="gap-4">
              {{#unless this.activeAccounts.length}}
                <AlertBanner
                  @title={{t "accounts.loan_summary.get_started"}}
                  data-test-loan-attention-link
                >
                  <:default>
                    {{t "accounts.loan_summary.get_started_body"}}
                  </:default>
                  <:action>
                    <Button
                      @route="authenticated.observability.upload"
                      @query={{this.uploadQueryParams}}
                      @intent="primary"
                      @size="sm"
                    >
                      {{t "accounts.loan_summary.upload_documents"}}
                    </Button>
                  </:action>
                </AlertBanner>
              {{/unless}}
              {{#if this.needsReceivingAccount}}
                <AlertBanner
                  @title={{t "accounts.loan_summary.attention.choose_account_title"}}
                  data-test-loan-attention-choose-account
                >
                  <:default>
                    {{t "accounts.loan_summary.attention.choose_account_body"}}
                  </:default>
                  <:action>
                    <Button
                      @intent="primary"
                      @size="sm"
                      {{on "click" this.toggleChangeAccountModal}}
                    >
                      {{t "accounts.loan_summary.attention.choose_account_cta"}}
                    </Button>
                  </:action>
                </AlertBanner>
              {{/if}}
              {{#each this.accountsInRelink as |account|}}
                <AlertBanner
                  @title={{t
                    "accounts.loan_summary.attention.relink_title"
                    servicer=account.institutionName
                  }}
                  data-test-loan-attention-relink
                >
                  <:default>
                    {{t "accounts.loan_summary.attention.relink_body"}}
                  </:default>
                  <:action>
                    <Button
                      @route="authenticated.observability.upload"
                      @query={{this.uploadQueryParams}}
                      @intent="primary"
                      @size="sm"
                    >
                      {{t "accounts.loan_summary.attention.relink_cta"}}
                    </Button>
                  </:action>
                </AlertBanner>
              {{/each}}
            </VStack>
          </:body>
        </Section>
      {{/if}}

      <ChangeActiveAccountModal
        @onClose={{this.toggleChangeAccountModal}}
        @show={{this.showChangeAccountModal}}
        @accountOptions={{this.activeAccounts}}
        @accountReceivingContributions={{this.person.accountReceivingContributions}}
        @personId={{this.person.id}}
        @route="authenticated.account-activity.accounts"
        @onAccountChanged={{this.reloadRecords}}
      />

      {{#if this.isBeta}}
        {{#if this.loanAccounts.length}}
          <Section data-test-loan-overview>
            <:header>{{t "accounts.loan_summary.overview"}}</:header>
            <:body>
              <div class="grid grid-cols-1 gap-4 md:grid-cols-4">
                <StatCard
                  @label={{t "accounts.original_amount"}}
                  @value={{this.money this.originalBalance}}
                />
                <StatCard
                  @label={{t "accounts.loan_summary.monthly_payment"}}
                  @value={{this.money this.monthlyPayment}}
                />
                <StatCard
                  @label={{t "accounts.loan_summary.average_interest_rate"}}
                  @value="{{this.averageInterestRate}}%"
                />
                <StatCard
                  @label={{t "accounts.loan_summary.linked_loans"}}
                  @value={{this.loanCount}}
                  @subtext={{t
                    "accounts.loan_summary.account_count"
                    count=this.activeAccounts.length
                  }}
                />
              </div>
            </:body>
          </Section>
        {{/if}}
      {{/if}}

      {{#if this.isBeta}}
        {{#if this.loanAccounts.length}}
          <Section data-test-loan-accounts>
            <:header>{{t "accounts.loan_summary.your_loan_accounts"}}</:header>
            <:actions>
              {{#if this.showContributionSelector}}
                <Button
                  @intent="primary"
                  @appearance="outlined"
                  {{on "click" this.toggleChangeAccountModal}}
                >
                  {{t "slr.change_contributions"}}
                </Button>
              {{/if}}
            </:actions>
            <:body>
              <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                {{#each this.loanAccounts as |loan|}}
                  <LoanAccountCard @loan={{loan}} />
                {{/each}}
              </div>
            </:body>
          </Section>
        {{/if}}
      {{else}}
        {{#if this.accountReceivingContributions}}
          <Section class="overflow-x-auto">
            <:header>
              {{t "slr.account_receiving_contributions"}}
            </:header>
            <:subHeader>
              <span class="italic">{{t "slr.account_receiving_contributions_sub_header"}}</span>
            </:subHeader>
            <:actions>
              {{#if this.showContributionSelector}}
                <Button
                  @intent="primary"
                  @appearance="outlined"
                  {{on "click" this.toggleChangeAccountModal}}
                >
                  {{t "slr.change_contributions"}}
                </Button>
              {{/if}}
            </:actions>
            <:body>
              <AccountTable
                @account={{this.accountReceivingContributions}}
                @isReceivingContributions={{true}}
                @onAccountChanged={{this.reloadRecords}}
              />
            </:body>
          </Section>
        {{/if}}
        {{#if this.otherActiveAccount}}
          <Section class="overflow-x-auto">
            <:header>
              {{t "common.accounts.active_accounts"}}
            </:header>
            <:body>
              <VStack class="gap-6">
                {{#each this.otherActiveAccount as |account|}}
                  <AccountTable
                    @account={{account}}
                    @isReceivingContributions={{false}}
                    @onAccountChanged={{this.reloadRecords}}
                  />
                {{/each}}
              </VStack>
            </:body>
          </Section>
        {{/if}}
      {{/if}}

      {{#unless this.isBeta}}
        <Section id="my-documents">
          <:header>{{t "accounts.my_documents"}}</:header>
          <:body>
            <DocumentsTable @person={{this.person}} />
          </:body>
        </Section>
      {{/unless}}
      {{#if this.paidAccounts.length}}
        <Section>
          <:header>{{t "accounts.paid_in_full"}}</:header>
          <:body>
            <Table>
              <:thead as |options|>
                <options.tr>
                  <options.th>{{t "accounts.servicer"}}</options.th>
                  <options.th>{{t "accounts.number_of_loans"}}</options.th>
                </options.tr>
              </:thead>
              <:tbody as |options|>
                {{#each this.paidAccounts as |account|}}
                  <options.tr>
                    <options.td>{{account.institutionName}}</options.td>
                    <options.td>{{account.activeLoans.length}}</options.td>
                  </options.tr>
                {{/each}}
              </:tbody>
            </Table>
          </:body>
        </Section>
      {{/if}}

      {{#if this.employerContributions}}
        <Section data-test-employer-contributions>
          <:header>{{t "accounts.loan_summary.employer_contributions"}}</:header>
          <:subHeader>
            {{t
              "accounts.loan_summary.employer_contributions_sub_header"
              employer=this.employerName
              amount=(this.money this.employerContributions.lifetime)
            }}
          </:subHeader>
          <:actions>
            <Button @route="authenticated.slr.dashboard" @intent="primary" @appearance="outlined">
              {{t "accounts.loan_summary.view_contribution_history"}}
            </Button>
          </:actions>
          <:body>
            <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
              <StatCard
                @label={{t "accounts.loan_summary.contributed_this_year"}}
                @value={{this.money this.employerContributions.yearToDate}}
              />
              <StatCard
                @label={{t "accounts.loan_summary.lifetime_contributions"}}
                @value={{this.money this.employerContributions.lifetime}}
              />
              <StatCard
                @label={{t "accounts.loan_summary.projected_monthly"}}
                @value={{this.money this.projectedMonthlyContribution}}
              />
            </div>
          </:body>
        </Section>
      {{/if}}

      {{#if this.isBeta}}
        {{#if this.hasBenefitCards}}
          <Section data-test-your-benefits>
            <:header>{{t "accounts.loan_summary.your_benefits"}}</:header>
            <:subHeader>{{t "accounts.loan_summary.your_benefits_sub_header"}}</:subHeader>
            <:body>
              <BenefitCards @rows={{@model.benefitRows}} />
            </:body>
          </Section>
        {{/if}}
      {{/if}}

    </VStack>
  </template>
}
